import React, { useEffect, useState } from 'react';
import { Check, Camera, Calendar, Image } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Camera size={48} className="mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-600">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing-large bg-gray-50">
        <div className="photo-container text-center">
          <h1 className="hero-title mb-6">Meine Services</h1>
          <p className="subtitle max-w-2xl mx-auto">
            Fotografie-Dienstleistungen fuer jeden Anlass
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing">
        <div className="photo-container">
          {services.length > 0 ? (
            <div className={`grid grid-cols-1 gap-8 ${services.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
              {services.map((service, index) => (
                <div 
                  key={service.id}
                  className="bg-white border-2 border-gray-100 rounded-lg p-10 hover:border-primary transition-all fade-in-up hover:shadow-lg"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-20 h-20 bg-primary text-white rounded-full flex items-center justify-center mb-6">
                    <Camera size={36} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold mb-4">{service.title}</h3>
                  <p className="body-text text-gray-600 mb-6 text-lg">{service.description}</p>
                  {service.features && service.features.length > 0 && (
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check size={20} className="text-primary mt-1 flex-shrink-0" />
                          <span className="body-text text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Link to="/kontakt" className="btn-outline w-full text-center">
                    Anfragen
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Keine Services verfügbar</p>
            </div>
          )}

          {/* CTA to Preise & Leistungen */}
          <div className="mt-16 text-center">
            <Link
              to="/preise"
              data-testid="services-view-preise-btn"
              className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            >
              Preise ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-spacing bg-gray-50">
        <div className="photo-container">
          <h2 className="section-title text-center">So funktioniert's</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                1
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Kontaktaufnahme</h3>
              <p className="body-text text-gray-600">
                Senden Sie mir eine Anfrage mit Ihren Wuenschen und Vorstellungen
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                2
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Beratung</h3>
              <p className="body-text text-gray-600">
                Wir besprechen Details, Location und Ihre individuellen Wuensche
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                3
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Fotoshooting</h3>
              <p className="body-text text-gray-600">
                Shooting mit modernster Ausruestung und Techniken
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                4
              </div>
              <h3 className="font-heading text-xl font-semibold mb-3">Lieferung</h3>
              <p className="body-text text-gray-600">
                Sie erhalten Ihre bearbeiteten Bilder in hoechster Qualitaet
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="section-spacing bg-white">
        <div className="photo-container">
          <h2 className="section-title text-center">Was Sie erhalten</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-12 rounded-lg text-center">
              <Calendar size={64} className="text-primary mx-auto mb-6" />
              <h3 className="font-heading text-2xl font-semibold mb-4">Flexible Termine</h3>
              <p className="body-text text-gray-600 text-lg">
                Termine nach Vereinbarung, auch am Wochenende moeglich
              </p>
            </div>
            <div className="bg-gray-50 p-12 rounded-lg text-center">
              <Image size={64} className="text-primary mx-auto mb-6" />
              <h3 className="font-heading text-2xl font-semibold mb-4">Hochaufloesende Bilder</h3>
              <p className="body-text text-gray-600 text-lg">
                Alle Bilder in voller Aufloesung, nachbearbeitet
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-gray-50">
        <div className="photo-container">
          <div className="bg-primary text-white rounded-2xl p-12 text-center max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
              Bereit fuer Ihr Projekt?
            </h2>
            <p className="subtitle text-gray-300 mb-8">
              Kontaktieren Sie mich fuer ein unverbindliches Angebot
            </p>
            <Link to="/kontakt" className="btn-outline border-white text-white hover:bg-white hover:text-primary">
              Jetzt anfragen
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
